import { useMemo } from "react";
import { useLazyQuery } from "@apollo/client";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import type { AdminDailyOverviewDay, AdminDailyOrderListData, AdminDailyOrderListVars } from "../model/types";
import { ADMIN_DAILY_ORDER_LIST } from "../api/admin-monthly-overview";
import {
  formatDateToYearMonth,
  formatDateToYearMonthDay,
  formatDateToYearMonthDayDayOfWeek,
} from "../../../../utils/date/format-date";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import { notifyError } from "../../../../utils/toast";

const calendarStyles = `
  .daily-overview-calendar .react-calendar {
    width: 100% !important;
    border: none !important;
    font-size: 13px !important;
  }

  .daily-overview-calendar .react-calendar__navigation button {
    min-width: 36px !important;
    background: transparent !important;
  }

  .daily-overview-calendar .react-calendar__tile {
    text-align: left !important;
    padding: 0.35rem 0.25rem !important;
    min-height: 72px !important;
  }

  .daily-overview-calendar .react-calendar__tile--active {
    background: #eff6ff !important;
    color: inherit !important;
    border-radius: 0.5rem !important;
  }

  .daily-overview-calendar .react-calendar__tile--now {
    background: #f3f4f6 !important;
    color: inherit !important;
    border-radius: 0.5rem !important;
  }

  .daily-overview-calendar .react-calendar__month-view__weekdays__weekday {
    text-transform: none !important;
    font-size: 12px !important;
    color: #6b7280 !important;
  }
`;

function formatKRW(value: number): string {
  return value.toLocaleString("ko-KR");
}

function formatTime(isoString: string): string {
  return new Date(isoString).toLocaleTimeString("ko-KR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone: "Asia/Seoul",
  });
}

interface DailyOverviewCalendarProps {
  yearMonth: string;
  days: AdminDailyOverviewDay[];
  loading: boolean;
  selectedDate: Date;
  onSelectDate: (date: Date) => void;
  onChangeYearMonth: (yearMonth: string, nextSelectedDate: Date) => void;
}

const SUMMARY_ROWS = [
  { key: "sellerAmount", label: "입점업체 결제금액" },
  { key: "consumerFee", label: "소비자수수료" },
  { key: "totalAmount", label: "마히 결제금액" },
  { key: "platformFee", label: "사장님수수료" },
  { key: "settlementAmount", label: "정산금액" },
] as const;

export default function DailyOverviewCalendar({
  yearMonth,
  days,
  loading,
  selectedDate,
  onSelectDate,
  onChangeYearMonth,
}: DailyOverviewCalendarProps) {
  const dayMap = useMemo(
    () => new Map(days.map((day) => [day.date, day])),
    [days]
  );

  const [fetchOrderList, { data: orderListData, loading: orderListLoading }] =
    useLazyQuery<AdminDailyOrderListData, AdminDailyOrderListVars>(
      ADMIN_DAILY_ORDER_LIST,
      {
        onError: (error) => {
          notifyError(error.message || "거래 내역 조회 중 오류가 발생했습니다.");
        },
      }
    );

  const selectedKey = formatDateToYearMonthDay(selectedDate, "-");
  const selectedDay = dayMap.get(selectedKey);
  const orders = orderListData?.adminDailyOrderList ?? [];

  const handleSelectDate = (date: Date) => {
    onSelectDate(date);
    const dateKey = formatDateToYearMonthDay(date, "-");
    fetchOrderList({ variables: { date: dateKey } });
  };

  return (
    <div className="space-y-4">
      <style>{calendarStyles}</style>

      <div className="daily-overview-calendar rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-3">
          <div className="font-semibold text-gray-900">일별 거래 현황</div>
          <div className="mt-1 text-xs text-gray-500">
            날짜를 클릭하면 해당 일의 거래 내역을 확인할 수 있습니다.
          </div>
        </div>

        <Calendar
          value={selectedDate}
          onChange={(value) => handleSelectDate(value as Date)}
          calendarType="gregory"
          className="w-full"
          activeStartDate={new Date(`${yearMonth}-01T00:00:00`)}
          onActiveStartDateChange={({ activeStartDate }) => {
            if (!activeStartDate) return;
            onChangeYearMonth(
              formatDateToYearMonth(activeStartDate, "-"),
              activeStartDate
            );
          }}
          tileContent={({ date, view }) => {
            if (view !== "month") return null;
            const key = formatDateToYearMonthDay(date, "-");
            const day = dayMap.get(key);

            return (
              <div className="mt-1 min-h-[32px] rounded bg-gray-50 px-1 py-1 leading-tight">
                {day ? (
                  <>
                    <div className="text-[10px] font-semibold text-blue-600">
                      {loading ? "-" : formatKRW(day.totalAmount)}
                    </div>
                    <div className="text-[10px] font-medium text-pink-500">
                      {loading ? "-" : formatKRW(day.actualAmount)}
                    </div>
                  </>
                ) : null}
              </div>
            );
          }}
        />
      </div>

      {/* 선택 일자 패널 */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm">
        <div className="mb-4 font-semibold text-gray-900">
          {formatDateToYearMonthDayDayOfWeek(selectedDate)}
        </div>

        {!selectedDay ? (
          <div className="text-gray-400">해당 날짜에 거래 내역이 없습니다.</div>
        ) : (
          <div className="space-y-5">
            {/* 합계 요약 테이블 */}
            <div className="overflow-hidden rounded-md border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">구분</th>
                    <th className="px-4 py-2 text-right font-semibold text-gray-700">합계</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {SUMMARY_ROWS.map((row, idx) => (
                    <tr
                      key={row.key}
                      className={
                        idx === 2
                          ? "bg-blue-50 font-semibold"
                          : idx === 4
                            ? "bg-green-50 font-semibold"
                            : ""
                      }
                    >
                      <td className="px-4 py-2 text-gray-800">{row.label}</td>
                      <td className="px-4 py-2 text-right text-gray-900">
                        {formatKRW(selectedDay[row.key])}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-right text-xs text-gray-500">
                거래 건수: {formatKRW(selectedDay.orderCount)}건
              </div>
            </div>

            {/* 개별 거래 내역 테이블 */}
            <div>
              <div className="mb-2 text-xs font-semibold text-gray-500">
                개별 거래 내역
              </div>
              {orderListLoading ? (
                <div className="flex justify-center py-6">
                  <LoadingSpinnerInline />
                </div>
              ) : orders.length === 0 ? (
                <div className="py-4 text-center text-xs text-gray-400">
                  거래 내역이 없습니다.
                </div>
              ) : (
                <div className="overflow-x-auto rounded-md border border-gray-200">
                  <table className="w-full min-w-[720px] text-xs">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="px-3 py-2 text-left font-semibold">시각</th>
                        <th className="px-3 py-2 text-left font-semibold">매장</th>
                        <th className="px-3 py-2 text-center font-semibold">유형</th>
                        <th className="px-3 py-2 text-right font-semibold">입점업체 결제금액</th>
                        <th className="px-3 py-2 text-right font-semibold">소비자수수료</th>
                        <th className="px-3 py-2 text-right font-semibold">마히 결제금액</th>
                        <th className="px-3 py-2 text-right font-semibold">사장님수수료</th>
                        <th className="px-3 py-2 text-right font-semibold">정산금액</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {orders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-3 py-2 text-gray-500">
                            {formatTime(order.soldAt)}
                          </td>
                          <td className="max-w-[120px] truncate px-3 py-2 text-gray-800">
                            {order.storeTitle}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                                order.orderType === "예약팩"
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {order.orderType}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900">
                            {formatKRW(order.sellerAmount)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900">
                            {formatKRW(order.consumerFee)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-blue-700">
                            {formatKRW(order.totalAmount)}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-900">
                            {formatKRW(order.platformFee)}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-green-700">
                            {formatKRW(order.settlementAmount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    {/* 합계 행 */}
                    <tfoot>
                      <tr className="border-t-2 border-gray-300 bg-gray-50 font-semibold text-gray-800">
                        <td className="px-3 py-2" colSpan={3}>합계</td>
                        <td className="px-3 py-2 text-right">{formatKRW(selectedDay.sellerAmount)}</td>
                        <td className="px-3 py-2 text-right">{formatKRW(selectedDay.consumerFee)}</td>
                        <td className="px-3 py-2 text-right text-blue-700">{formatKRW(selectedDay.totalAmount)}</td>
                        <td className="px-3 py-2 text-right">{formatKRW(selectedDay.platformFee)}</td>
                        <td className="px-3 py-2 text-right text-green-700">{formatKRW(selectedDay.settlementAmount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
